// External libraries
import PropTypes from 'prop-types';
import React, { PureComponent } from 'react';
import styled, { css } from 'styled-components';

// Zocdoc libraries
import { grey25, navy, white } from '@zocdoc/branding/colors';
import {
    RadioActive,
    RadioHover,
    RadioInactive,
} from '@zocdoc-frontend-common/assets-icons';
import { FontFamily } from '@zocdoc-frontend-common/styles-typography';
import { breakpoint } from '@zocdoc-frontend-common/styles-grid';

export class Radio extends PureComponent {
    static propTypes = {
        flush: PropTypes.bool,
        innerMargin: PropTypes.number,
        renderWithBottomBorder: PropTypes.bool,
        renderWithoutBox: PropTypes.bool,
    };

    static defaultProps = {
        flush: false,
        innerMargin: 10,
        renderWithBottomBorder: false,
        renderWithoutBox: false,
    };

    render() {
        const {
            boldSelected,
            dataTestSelector,
            innerMargin,
            name,
            onChange,
            selectedValue,
            text,
            trackOnClick,
            value,
        } = this.props;

        // Becomes a controlled input if anything besides undefined is passed
        // as selected value
        let selected =
            selectedValue === undefined ? undefined : value === selectedValue;

        // Radio button's selected values are always strings.  This becomes problem when
        // the desired value is an int - so we try and see if they're both numbers, and if
        // they are, we compare those values explicitly.
        const valueParsed = parseInt(value, 10);
        const selectedValueParsed = parseInt(selectedValue, 10);
        if (!isNaN(valueParsed) && !isNaN(selectedValueParsed)) {
            selected = valueParsed === selectedValueParsed;
        }

        return (
            <RadioContainer {...this.props}>
                <RadioButton>
                    <RadioInput
                        checked={selected}
                        data-test={dataTestSelector}
                        name={name}
                        onChange={onChange}
                        onClick={trackOnClick}
                        onKeyDown={this.handleKeyDown}
                        type="radio"
                        value={value}
                    />
                    <RadioIcon data-test="radio-icon" innerMargin={innerMargin}>
                        {selected ? (
                            <RadioActive
                                backgroundColor={
                                    this.props.radioBackgroundColor
                                }
                                color={this.props.radioColor}
                                height={16}
                            />
                        ) : (
                            <RadioInactive
                                backgroundColor={
                                    this.props.radioBackgroundColor
                                }
                                color={this.props.radioColor}
                                height={16}
                            />
                        )}
                    </RadioIcon>
                    <RadioIconHovered
                        data-test="radio-icon-hovered"
                        innerMargin={innerMargin}
                    >
                        {selected ? (
                            <RadioActive
                                backgroundColor={
                                    this.props.radioBackgroundColor
                                }
                                color={this.props.radioColor}
                                height={16}
                            />
                        ) : (
                            <RadioHover
                                backgroundColor={
                                    this.props.radioBackgroundColor
                                }
                                color={this.props.radioColor}
                                height={16}
                            />
                        )}
                    </RadioIconHovered>
                    <RadioText
                        boldSelected={boldSelected}
                        data-test="radio-text"
                        selected={selected}
                    >
                        {text}
                    </RadioText>
                </RadioButton>
            </RadioContainer>
        );
    }

    // perform tracking when user is using the keyboard to navigate between tabs
    handleKeyDown = e => {
        const { trackOnKeyDown } = this.props;
        switch (e.key) {
            case 'ArrowUp':
            case 'ArrowRight':
            case 'ArrowDown':
            case 'ArrowLeft':
            case 'Enter':
            case 'Space':
                if (trackOnKeyDown) {
                    trackOnKeyDown(e);
                }
                break;
            // no default
        }
    };
}

const RadioButton = styled.div`
    display: flex;
    align-items: center;

    position: relative;
    line-height: 0;

    cursor: pointer;
`;

const RadioIcon = styled.div`
    pointer-events: none;
    margin-right: ${p => p.innerMargin}px;
`;

const RadioIconHovered = styled(RadioIcon)`
    display: none;
`;

export const RadioText = styled.span`
    display: inline-block;
    pointer-events: none;
    color: ${navy};
    font-size: 13px;
    line-height: 24px;
    font-family: ${p =>
        p.boldSelected === true && p.selected === true
            ? FontFamily.SharpSansBold
            : FontFamily.SharpSansSemibold};

    ${breakpoint.medium} {
        font-size: 15px;
    }
`;

const RadioInput = styled.input`
    position: absolute;
    display: block;
    appearance: none;
    margin: 0;

    overflow: hidden;
    cursor: pointer;
    opacity: 0;

    &:focus ~ ${RadioIcon} > *,
    &:focus ~ ${RadioIconHovered} > * {
        box-shadow: 0 0 5px 3px #5098ff;
        border-radius: 8px;
    }
`;

const RadioContainer = styled.label`
    box-sizing: border-box;

    display: flex;
    flex-grow: 1;
    align-items: center;

    height: 48px;
    position: relative;
    padding: 16px;
    white-space: nowrap;

    cursor: pointer;
    background-color: ${p =>
        p.renderWithoutBox || p.renderWithBottomBorder
            ? 'transparent'
            : `${white}`};

    &:hover ${RadioIcon} {
        display: none;
    }

    &:hover ${RadioIconHovered} {
        display: block;
    }

    ${p =>
        p.flush
            ? css`
                  padding-left: 0;
                  padding-right: 0;
              `
            : ''}

    ${p =>
        p.renderWithBottomBorder &&
        css`
            border-bottom: solid 1px ${grey25};
            :last-of-type {
                border-bottom: none;
            }
        `}
`;
